import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Home, Languages } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations/canvaTranslations';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'hero', label: t.navbar.overview },
    { id: 'problem', label: t.navbar.problem },
    { id: 'solution', label: t.navbar.solution },
    { id: 'workflow', label: t.navbar.workflow },
    { id: 'ai', label: t.navbar.ai },
    { id: 'technical', label: t.navbar.technical },
    { id: 'conclusion', label: t.navbar.conclusion },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Home Link */}
          <Link
            to="/"
            className="flex items-center space-x-2 text-gray-700 hover:text-[#00C4CC] transition-colors"
          >
            <Home className="w-5 h-5" />
            <span className="font-medium">{t.navbar.backToHome}</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isScrolled
                    ? 'text-gray-700 hover:bg-gray-100 hover:text-[#00C4CC]'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                {item.label}
              </button>
            ))}
            
            {/* Language Toggle Button */}
            <button
              onClick={toggleLanguage}
              className={`ml-4 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                isScrolled
                  ? 'text-gray-700 hover:bg-gray-100 hover:text-[#00C4CC]'
                  : 'text-white hover:bg-white/10'
              }`}
              title={language === 'en' ? '切换到中文' : 'Switch to English'}
            >
              <Languages className="w-4 h-4" />
              <span>{language === 'en' ? '中文' : 'EN'}</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isScrolled
                ? 'text-gray-700 hover:bg-gray-100'
                : 'text-white hover:bg-white/10'
            }`}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-[#00C4CC] rounded-lg transition-colors"
              >
                {item.label}
              </button>
            ))}
            {/* Language Toggle Button for Mobile */}
            <button
              onClick={toggleLanguage}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-[#00C4CC] rounded-lg transition-colors flex items-center space-x-2 mt-2"
            >
              <Languages className="w-4 h-4" />
              <span>{language === 'en' ? '切换到中文' : 'Switch to English'}</span>
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;

